import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Dialog, DialogTitle, DialogContent, List, ListItem, ListItemText, IconButton,
    ListItemButton, ListSubheader, Divider, CircularProgress, Box
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import API from '@utils/axios';


function WorkflowCreationDialog({ type, modalCreateOpen, setModalCreateOpen }) {
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [customWorkflows, setCustomWorkflows] = useState([]);
    const [templateWorkflows, setTemplateWorkflows] = useState([]);

    // Chargement de la liste des workflows
    useEffect(() => {
        const fetchWorkflows = async () => {
            try {
                const response = await API.get('/workflows', {
                    params: {
                        sort: `name.${i18n.language}`,
                        direction: 'asc',
                        per_page: 9999,
                    }
                });
                if (response.data && Array.isArray(response.data.data)) {
                    setCustomWorkflows(response.data.data.filter(workflow => workflow.customer_id !== null));
                    setTemplateWorkflows(response.data.data.filter(workflow => workflow.customer_id === null));
                } else {
                    setCustomWorkflows([]);
                    setTemplateWorkflows([]);
                }
            } catch (error) {
                setCustomWorkflows([]);
                setTemplateWorkflows([]);
            } finally {
                setLoading(false);
            }
        };
        if (modalCreateOpen) {
            fetchWorkflows();
        }
    }, [modalCreateOpen, i18n.language]);

    // A la fermeture de la modale, on réinitialise les données
    useEffect(() => {
        if (!modalCreateOpen) {
            setLoading(true);
            setCustomWorkflows([]);
            setTemplateWorkflows([]);
        }
    }, [modalCreateOpen]);

    return (
        <Dialog
            open={modalCreateOpen}
            onClose={() => setModalCreateOpen(false)}
            aria-labelledby="parent-modal-title"
        >
            <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                {type === "custom" ? t('Create custom workflow') : t('Create workflow template')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => setModalCreateOpen(false)}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent dividers sx={{ p: 0, width: '400px' }}>
                <List sx={{ width: '100%', bgcolor: 'background.paper', height: '400px', overflow: 'auto', m: 0, p: 0 }}>
                    <ListItem disablePadding>
                        <ListItemButton component={RouterLink} to={`/workflows/${type}/create`}>
                            <ListItemText primary={t('Empty workflow')}
                                secondary={t('Create a new workflow from scratch')} />
                        </ListItemButton>
                    </ListItem>
                    {loading ? (
                        <ListItem disablePadding>
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', pt: 4 }}>
                                <CircularProgress />
                            </Box>
                        </ListItem>
                    ) : (
                        <div>
                            {templateWorkflows.length > 0 && (
                                <div>
                                    <Divider component="li" />
                                    <ListSubheader>{t('Create from a template')}</ListSubheader>
                                    {templateWorkflows.map((workflow) => (
                                        <ListItem key={workflow.id} disablePadding>
                                            <ListItemButton component={RouterLink} to={`/workflows/${type}/create?template=${workflow.id}`}>
                                                <ListItemText primary={workflow.name[i18n.language]}
                                                    secondary={workflow.description[i18n.language]
                                                        ? workflow.description[i18n.language] : t('No description')} />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </div>
                            )}
                            {customWorkflows.length > 0 && (
                                <div>
                                    <Divider component="li" />
                                    <ListSubheader>{t('Duplicate an existing workflow')}</ListSubheader>
                                    {customWorkflows.map((workflow) => (
                                        <ListItem key={workflow.id} disablePadding>
                                            <ListItemButton component={RouterLink} to={`/workflows/${type}/create?template=${workflow.id}`}>
                                                <ListItemText primary={workflow.name.fr}
                                                    secondary={workflow.description.fr ? workflow.description.fr : t('No description')} />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </List>
            </DialogContent>
        </Dialog>
    );

}

export default WorkflowCreationDialog;